/**
 *  Component Helpers
 */
 export function guid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Theme Helpers
 */
export const _COLORS_ = Object.freeze({
  0: 'primary',
  1: 'secondary',
  2: 'tertiary',
  4: 'quarternary',
  5: 'quinary',
  6: 'senary',
  7: 'gray-lite',
  8: 'gray-dark',
});

/**
 * Window Helpers
 */

// Keyboard Enum
export const _KEYBOARD_ = Object.freeze({
  'enter': 13,
  'arrow-u': 38,
  'arrow-r': 39,
  'arrow-d': 40,
  'arrow-l': 37,
  'esc': 27,
  'tab': 9,
  'space': 32
});

/**
 * Breakpoints are situatued such that
 * comparison would be "and down" --
 * 
 * if (window.innerWidth < _BREAKPOINTS_.sm) { ... }
 */
export const _BREAKPOINTS_ = Object.freeze({
  'xs': 576,
  'sm': 767,
  'md': 991,
  'lg': 1199,
  'xl': 1399,
  'giant': 1799
});

export const clamp = (min: number, x: number, max: number) =>
	Math.max(min, Math.min(x, max));

/** JS's `%` operator is the remainder operator. This is the real modulo function. */
export const mod = (n: number, d: number) =>
	((n % d) + d) % d;

export const domRectCenter = (rect: DOMRectReadOnly) => ({
	x: rect.left + rect.width  / 2,
	y: rect.top  + rect.height / 2
});

export const domRectIntersection = (a: DOMRectReadOnly, b: DOMRectReadOnly) => {
	const left   = Math.max(a.left,   b.left  );
	const right  = Math.min(a.right,  b.right );
	const top    = Math.max(a.top,    b.top   );
	const bottom = Math.min(a.bottom, b.bottom);

	if (left <= right && top <= bottom)
		return new DOMRectReadOnly(
			left,
			top,
			right  - left,
			bottom - top
		);
};

export const domRectIntersectionRatio = (container: DOMRectReadOnly, target: DOMRectReadOnly) => {
	const targetArea = target.width * target.height;
	const intersection = domRectIntersection(container, target);
	if (!intersection || targetArea === 0)
		return 0;

	return (intersection.width * intersection.height) / targetArea;
};

export const distanceFromPointToPoint = (a: { x: number, y: number }, b: { x: number, y: number }) =>
	Math.hypot(
		b.x - a.x,
		b.y - a.y
	);

  export const wait = <T>(ms: number, value?: T) =>
    new Promise(resolve => {
      setTimeout(() => {
        resolve(value);
      }, ms)
    });
  
  export const deferredPromise = <Resolved = unknown, Rejected = never>() => {
    const result: {
      resolve: (x: Resolved) => void,
      reject:  (x: Rejected) => void,
      promise: Promise<Resolved | Rejected>
    } = {} as any;
    result.promise = new Promise((resolve, reject) => {
      result.resolve = resolve;
      result.reject  = reject;
    });
    return result;
  };
  
  export const debounce = <F extends (...args: Array<unknown>) => unknown>(f: F, ms = 100) => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
  
    return (...args: Parameters<F>) => {
      if (timeout !== undefined)
        clearTimeout(timeout);
  
      timeout =
        setTimeout(() => {
          timeout = undefined;
          f(...args);
        }, ms);
    };
  };
  
  export const throttle = <F extends (...args: Array<unknown>) => unknown>(f: F, ms = 100) => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let lastArgs: Parameters<F> | undefined;
  
    return (...args: Parameters<F>) => {
      lastArgs = args;
  
      if (timeout !== undefined)
        return;
  
      timeout =
        setTimeout(() => {
          timeout = undefined;
          f(...lastArgs!);
        }, ms);
    };
  };

  export const distanceFromPointToDomRect = (point: { x: number, y: number }, rect: DOMRectReadOnly) =>
    Math.hypot(
      Math.max(
        rect.left - point.x,
        0,
        point.x - rect.right
      ),
      Math.max(
        rect.top - point.y,
        0,
        point.y - rect.bottom
      )
    );

export const isOnScreen = function (el) {
  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;

  // Only completely visible elements return true
  return elemTop < window.innerHeight - 100 && elemBottom >= 0;
}

export const getQueryStringValue = (key) => {  
  return decodeURIComponent(window.location.search.replace(new RegExp("^(?:.*[&\\?]" + encodeURIComponent(key).replace(/[\.\+\*]/g, "\\$&") + "(?:\\=([^&]*))?)?.*$", "i"), "$1"));  
}  

export const scrollToTop = function() {
  window.scrollTo({
    behavior: 'smooth',
    top: 0,
  });
}

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

export function setCookie(cname, cvalue, exdays) {
  const d = new Date();
  d.setTime(d.getTime() + (exdays*24*60*60*1000));
  const expires = "expires="+d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

export function isScrolledIntoView(el, ratioVisible: number = 1): boolean {
  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const elemBottom = rect.bottom;
  const offsetHeight = elemTop + (el.offsetHeight * ratioVisible);

  // Only completely visible elements return true:
  const isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);
  // Partially visible elements return true:
  const isPartiallyVisible = offsetHeight >= 0 && offsetHeight < window.innerHeight;
  
  return isVisible || isPartiallyVisible;
};
